import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import Header from "@/components/layout/Header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Plus, Edit, Search, Trash2, Check, ChevronsUpDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { cn } from "@/lib/utils";
import StarRating from "@/components/seo/StarRating";
import GooglePlacesAutocomplete from "@/components/GooglePlacesAutocomplete";
import GoogleDestinationMap from "@/components/GoogleDestinationMap";
import AdminCSVCountryImport from "@/components/dashboard/AdminCSVCountryImport";
import AdminCSVCountryExport from "@/components/dashboard/AdminCSVCountryExport";
import AdminImportExportHistory from "@/components/dashboard/AdminImportExportHistory";

interface Country {
  id: string;
  name: string;
  city: string | null;
  flag_url: string | null;
  banner_url: string | null;
  tagline: string | null;
  tourist_stay_default: string | null;
  remote_work_visa_option: string | null;
  key_requirements: string | null;
  tax_residency_trigger: string | null;
  notes_1_5_months: number | null;
  ease_1_5_months: number | null;
  ease_compliance: number | null;
  cost_of_living: number | null;
  attractiveness_rating: number | null;
  employee_notes: string | null;
  legal_compliance_risks: string | null;
  hr_guidelines: string | null;
  cost_impact_estimate: string | null;
  company_caps_observed: string | null;
  weekly_cost_excluding_flight: number | null;
  round_trip_flight_cost_from_france: number | null;
  image_url: string | null;
  renest_notes: string | null;
  is_active: boolean;
  total_score: number | null;
}

interface Destination {
  id: string;
  name: string;
  status: string;
  date_created: string;
}

const AdminDestinations = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  
  // Countries state
  const [countries, setCountries] = useState<Country[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const [isCountryDialogOpen, setIsCountryDialogOpen] = useState(false);
  const [editingCountry, setEditingCountry] = useState<Country | null>(null);
  const [countryFormData, setCountryFormData] = useState<Partial<Country>>({
    is_active: true,
  });

  // Destinations state (legacy map pins)
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [isDestinationDialogOpen, setIsDestinationDialogOpen] = useState(false);
  const [editingDestination, setEditingDestination] = useState<Destination | null>(null);
  const [destinationFormData, setDestinationFormData] = useState({
    name: "",
    status: "open"
  });

  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);

  useEffect(() => {
    checkAuthorization();
  }, []);

  const checkAuthorization = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate("/auth");
      return;
    }

    const { data: profile } = await supabase
      .from("profiles")
      .select("id")
      .eq("user_id", user.id)
      .single();

    if (!profile) {
      toast({ title: "Erreur", description: "Profil non trouvé", variant: "destructive" });
      navigate("/");
      return;
    }

    const { data: roles } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id)
      .eq("role", "admin");

    if (!roles || roles.length === 0) {
      toast({ title: "Accès refusé", description: "Vous devez être administrateur", variant: "destructive" });
      navigate("/");
      return;
    }

    setAuthorized(true);
    fetchCountries();
    fetchDestinations();
  };

  const fetchCountries = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("countries")
      .select("*")
      .order("name");

    if (error) {
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
    } else {
      setCountries(data || []);
    }
    setLoading(false);
  };

  const fetchDestinations = async () => {
    const { data, error } = await supabase
      .from("destinations")
      .select("*")
      .order("name");

    if (error) {
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
    } else {
      setDestinations(data || []);
    }
  };

  const handleCountrySave = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data: profile } = await supabase
      .from("profiles")
      .select("id")
      .eq("user_id", user.id)
      .single();

    if (editingCountry) {
      // Exclude total_score from updates as it's a calculated field
      const { total_score, ...updateData } = countryFormData;
      const { error } = await supabase
        .from("countries")
        .update(updateData)
        .eq("id", editingCountry.id);

      if (error) {
        toast({ title: "Erreur", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "Succès", description: "Pays mis à jour" });
        fetchCountries();
        resetCountryForm();
      }
    } else {
      const dataToInsert: any = { ...countryFormData };
      if (profile?.id) {
        dataToInsert.created_by = profile.id;
      }
      
      const { error } = await supabase
        .from("countries")
        .insert(dataToInsert);

      if (error) {
        toast({ title: "Erreur", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "Succès", description: "Pays créé" });
        fetchCountries();
        resetCountryForm();
      }
    }
  };

  const handleCountryEdit = (country: Country) => {
    setEditingCountry(country);
    setCountryFormData(country);
    setIsCountryDialogOpen(true);
  };

  const handleCountryDelete = async (id: string) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer ce pays ?")) return;

    const { error } = await supabase
      .from("countries")
      .delete()
      .eq("id", id);

    if (error) {
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Succès", description: "Pays supprimé" });
      fetchCountries();
    }
  };

  const resetCountryForm = () => {
    setEditingCountry(null);
    setCountryFormData({ is_active: true });
    setIsCountryDialogOpen(false);
  };

  const handleDestinationSave = async () => {
    if (editingDestination) {
      const { error } = await supabase
        .from("destinations")
        .update(destinationFormData)
        .eq("id", editingDestination.id);

      if (error) {
        toast({ title: "Erreur", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "Succès", description: "Destination mise à jour" });
        fetchDestinations();
        resetDestinationForm();
      }
    } else {
      const { error } = await supabase
        .from("destinations")
        .insert(destinationFormData);

      if (error) {
        toast({ title: "Erreur", description: error.message, variant: "destructive" });
      } else {
        toast({ title: "Succès", description: "Destination créée" });
        fetchDestinations();
        resetDestinationForm();
      }
    }
  };

  const handleDestinationEdit = (destination: Destination) => {
    setEditingDestination(destination);
    setDestinationFormData({
      name: destination.name,
      status: destination.status
    });
    setIsDestinationDialogOpen(true);
  };

  const handleDestinationDelete = async (id: string) => {
    if (!confirm("Êtes-vous sûr de vouloir supprimer cette destination ?")) return;

    const { error } = await supabase
      .from("destinations")
      .delete()
      .eq("id", id);

    if (error) {
      toast({ title: "Erreur", description: error.message, variant: "destructive" });
    } else {
      toast({ title: "Succès", description: "Destination supprimée" });
      fetchDestinations();
    }
  };

  const resetDestinationForm = () => {
    setEditingDestination(null);
    setDestinationFormData({ name: "", status: "open" });
    setIsDestinationDialogOpen(false);
  };

  const filteredCountries = countries.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!authorized || loading) {
    return <div className="flex items-center justify-center min-h-screen">Chargement...</div>;
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto px-4 py-8 pt-24">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold">Gestion des destinations & pays</h1>
            <p className="text-muted-foreground mt-2">Gérez les pays disponibles et les destinations sur la carte</p>
          </div>
        </div>

        <Tabs defaultValue="countries" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8">
            <TabsTrigger value="countries">Pays (Catalogue complet)</TabsTrigger>
            <TabsTrigger value="map">Carte des destinations</TabsTrigger>
          </TabsList>

          {/* Countries Management Tab */}
          <TabsContent value="countries" className="space-y-6">
            <div className="flex justify-between items-center gap-4 mb-4">
              <div className="flex gap-2">
                <AdminCSVCountryImport onImportComplete={fetchCountries} />
                <AdminCSVCountryExport />
              </div>
            </div>

            <div className="flex justify-between items-center gap-4">
              <Popover open={searchOpen} onOpenChange={setSearchOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={searchOpen}
                    className="flex-1 max-w-md justify-between"
                  >
                    <Search className="mr-2 h-4 w-4 shrink-0 opacity-50" />
                    <span className="flex-1 text-left truncate">
                      {searchTerm || "Rechercher un pays..."}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[400px] p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Rechercher un pays..." />
                    <CommandList>
                      <CommandEmpty>Aucun pays trouvé.</CommandEmpty>
                      <CommandGroup>
                        {countries.map((country) => (
                          <CommandItem
                            key={country.id}
                            value={country.name}
                            onSelect={(currentValue) => {
                              setSearchTerm(currentValue === searchTerm ? "" : currentValue);
                              setSearchOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                searchTerm === country.name ? "opacity-100" : "opacity-0"
                              )}
                            />
                            {country.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              <Button onClick={() => setIsCountryDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Ajouter un pays
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredCountries.map((country) => (
                <Card key={country.id} className="p-6 hover:shadow-lg transition-shadow">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-semibold">{country.name}</h3>
                      <p className="text-sm text-muted-foreground">{country.tagline}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="icon" onClick={() => handleCountryEdit(country)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleCountryDelete(country.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                  {country.total_score !== null && (
                    <div className="mb-2">
                      <p className="text-sm font-medium">Score: {country.total_score}/20</p>
                      <StarRating rating={Math.round((country.total_score / 20) * 5)} />
                    </div>
                  )}
                  <div className="flex items-center gap-2 mt-4">
                    <span className="text-sm">{country.is_active ? "Actif" : "Inactif"}</span>
                    <div className={`w-2 h-2 rounded-full ${country.is_active ? "bg-green-500" : "bg-gray-300"}`} />
                  </div>
                </Card>
              ))}
            </div>

            {filteredCountries.length === 0 && (
              <Card className="p-12 text-center">
                <p className="text-muted-foreground">Aucun pays trouvé</p>
              </Card>
            )}

            <div className="mt-8">
              <AdminImportExportHistory />
            </div>
          </TabsContent>

          {/* Map Destinations Tab */}
          <TabsContent value="map" className="space-y-6">
            <div className="flex justify-between items-center">
              <p className="text-muted-foreground">Gérez les destinations affichées sur la carte publique</p>
              <Button onClick={() => setIsDestinationDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Ajouter une destination
              </Button>
            </div>

            <div className="bg-white rounded-2xl shadow-2xl border overflow-hidden mb-6">
              <div className="h-[500px] w-full">
                <GoogleDestinationMap />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {destinations.map((destination) => (
                <Card key={destination.id} className="p-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold">{destination.name}</h3>
                      <p className="text-sm text-muted-foreground capitalize">{destination.status}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" size="icon" onClick={() => handleDestinationEdit(destination)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDestinationDelete(destination.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>

            {destinations.length === 0 && (
              <Card className="p-12 text-center">
                <p className="text-muted-foreground">Aucune destination sur la carte</p>
              </Card>
            )}
          </TabsContent>
        </Tabs>

        {/* Country Dialog */}
        <Dialog open={isCountryDialogOpen} onOpenChange={setIsCountryDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingCountry ? "Modifier le pays" : "Ajouter un pays"}
              </DialogTitle>
            </DialogHeader>

            <Tabs defaultValue="employee" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="employee">Info Employés</TabsTrigger>
                <TabsTrigger value="company">Info Entreprise</TabsTrigger>
              </TabsList>

              <TabsContent value="employee" className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <Label>Nom du pays *</Label>
                    <GooglePlacesAutocomplete
                      onPlaceSelected={(placeName) => setCountryFormData({ ...countryFormData, name: placeName })}
                      placeholder="Rechercher un pays..."
                      initialValue={editingCountry?.name || countryFormData.name || ""}
                    />
                    {countryFormData.name && (
                      <p className="text-sm text-muted-foreground mt-2">
                        Pays sélectionné: <strong>{countryFormData.name}</strong>
                      </p>
                    )}
                  </div>
                  <div>
                    <Label>Ville</Label>
                    <Input
                      value={countryFormData.city || ""}
                      onChange={(e) => setCountryFormData({ ...countryFormData, city: e.target.value })}
                      placeholder="Paris, Barcelone, etc."
                    />
                  </div>
                  <div>
                    <Label>Statut</Label>
                    <Input
                      value={countryFormData.is_active ? "Ouvert" : "Fermé"}
                      disabled
                      className="bg-muted"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Slogan</Label>
                    <Input
                      value={countryFormData.tagline || ""}
                      onChange={(e) => setCountryFormData({ ...countryFormData, tagline: e.target.value })}
                      placeholder="Soleil, mer et télétravail"
                    />
                  </div>
                  <div>
                    <Label>Séjour touristique (défaut)</Label>
                    <Input
                      value={countryFormData.tourist_stay_default || ""}
                      onChange={(e) => setCountryFormData({ ...countryFormData, tourist_stay_default: e.target.value })}
                      placeholder="90 jours"
                    />
                  </div>
                  <div>
                    <Label>Visa remote / durée</Label>
                    <Input
                      value={countryFormData.remote_work_visa_option || ""}
                      onChange={(e) => setCountryFormData({ ...countryFormData, remote_work_visa_option: e.target.value })}
                      placeholder="Digital Nomad Visa (1 an, renouvelable)"
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Exigences clés</Label>
                    <Textarea
                      value={countryFormData.key_requirements || ""}
                      onChange={(e) => setCountryFormData({ ...countryFormData, key_requirements: e.target.value })}
                      placeholder="Passeport valide, preuve de revenus, etc."
                      rows={2}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Enregistrement / Règles fiscales</Label>
                    <Input
                      value={countryFormData.tax_residency_trigger || ""}
                      onChange={(e) => setCountryFormData({ ...countryFormData, tax_residency_trigger: e.target.value })}
                      placeholder=">183 jours"
                    />
                  </div>
                  <div>
                    <Label>Ease 1–5m (1-5 ⭐)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="5"
                      value={countryFormData.ease_1_5_months || ""}
                      onChange={(e) => setCountryFormData({ ...countryFormData, ease_1_5_months: parseInt(e.target.value) })}
                    />
                  </div>
                  <div>
                    <Label>Ease Compliance (1-5 ⭐)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="5"
                      value={countryFormData.ease_compliance || ""}
                      onChange={(e) => setCountryFormData({ ...countryFormData, ease_compliance: parseInt(e.target.value) })}
                    />
                  </div>
                  <div>
                    <Label>Coût de vie (1-5 ⭐)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="5"
                      value={countryFormData.cost_of_living || ""}
                      onChange={(e) => setCountryFormData({ ...countryFormData, cost_of_living: parseInt(e.target.value) })}
                    />
                  </div>
                  <div>
                    <Label>Attractivité cadres (1-5 ⭐)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="5"
                      value={countryFormData.attractiveness_rating || ""}
                      onChange={(e) => setCountryFormData({ ...countryFormData, attractiveness_rating: parseInt(e.target.value) })}
                    />
                  </div>
                  <div>
                    <Label>Notes 1-5 Months (1-5 ⭐)</Label>
                    <Input
                      type="number"
                      min="1"
                      max="5"
                      value={countryFormData.notes_1_5_months || ""}
                      onChange={(e) => setCountryFormData({ ...countryFormData, notes_1_5_months: parseInt(e.target.value) })}
                    />
                  </div>
                  <div className="col-span-2">
                    <Label>Employee Notes</Label>
                    <Textarea
                      value={countryFormData.employee_notes || ""}
                      onChange={(e) => setCountryFormData({ ...countryFormData, employee_notes: e.target.value })}
                      placeholder="Notes visibles par les employés..."
                      rows={3}
                    />
                  </div>
                  <div>
                    <Label>Coût hebdo (hors vol) €</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={countryFormData.weekly_cost_excluding_flight || ""}
                      onChange={(e) => setCountryFormData({ ...countryFormData, weekly_cost_excluding_flight: parseFloat(e.target.value) })}
                      placeholder="650"
                    />
                  </div>
                  <div>
                    <Label>A/R depuis FR (est.) €</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={countryFormData.round_trip_flight_cost_from_france || ""}
                      onChange={(e) => setCountryFormData({ ...countryFormData, round_trip_flight_cost_from_france: parseFloat(e.target.value) })}
                      placeholder="800"
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="company" className="space-y-4">
                <div>
                  <Label>Impact coût (entreprise)</Label>
                  <Textarea
                    value={countryFormData.cost_impact_estimate || ""}
                    onChange={(e) => setCountryFormData({ ...countryFormData, cost_impact_estimate: e.target.value })}
                    placeholder="Estimation de l'impact coût pour l'entreprise..."
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Risques juridiques</Label>
                  <Textarea
                    value={countryFormData.legal_compliance_risks || ""}
                    onChange={(e) => setCountryFormData({ ...countryFormData, legal_compliance_risks: e.target.value })}
                    placeholder="Détails des risques légaux..."
                    rows={3}
                  />
                </div>
                <div>
                  <Label>Lignes directrices RH</Label>
                  <Textarea
                    value={countryFormData.hr_guidelines || ""}
                    onChange={(e) => setCountryFormData({ ...countryFormData, hr_guidelines: e.target.value })}
                    placeholder="Recommandations pour les RH..."
                    rows={3}
                  />
                </div>
                <div>
                  <Label>Caps d'entreprise (observés)</Label>
                  <Textarea
                    value={countryFormData.company_caps_observed || ""}
                    onChange={(e) => setCountryFormData({ ...countryFormData, company_caps_observed: e.target.value })}
                    placeholder="Limites habituellement observées par les entreprises..."
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Image URL</Label>
                  <Input
                    value={countryFormData.image_url || ""}
                    onChange={(e) => setCountryFormData({ ...countryFormData, image_url: e.target.value })}
                    placeholder="https://example.com/image.jpg"
                  />
                </div>
                <div>
                  <Label>Notes Renest</Label>
                  <Textarea
                    value={countryFormData.renest_notes || ""}
                    onChange={(e) => setCountryFormData({ ...countryFormData, renest_notes: e.target.value })}
                    placeholder="Notes internes Renest..."
                    rows={3}
                  />
                </div>
              </TabsContent>
            </Tabs>

            <div className="flex items-center justify-between pt-4 border-t">
              <div className="flex items-center gap-2">
                <Switch
                  checked={countryFormData.is_active}
                  onCheckedChange={(checked) => setCountryFormData({ ...countryFormData, is_active: checked })}
                />
                <Label>Pays actif</Label>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={resetCountryForm}>
                  Annuler
                </Button>
                <Button onClick={handleCountrySave} disabled={!countryFormData.name}>
                  {editingCountry ? "Mettre à jour" : "Créer"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Destination Dialog (Legacy Map Pins) */}
        <Dialog open={isDestinationDialogOpen} onOpenChange={setIsDestinationDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                {editingDestination ? "Modifier la destination" : "Ajouter une destination"}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              <div>
                <Label>Nom de la destination *</Label>
                <GooglePlacesAutocomplete
                  onPlaceSelected={(placeName) => setDestinationFormData({ ...destinationFormData, name: placeName })}
                  placeholder="Rechercher une ville..."
                />
                {destinationFormData.name && (
                  <p className="text-sm text-muted-foreground mt-2">
                    Destination sélectionnée: <strong>{destinationFormData.name}</strong>
                  </p>
                )}
              </div>
              <div>
                <Label>Statut</Label>
                <Input
                  value={destinationFormData.status}
                  onChange={(e) => setDestinationFormData({ ...destinationFormData, status: e.target.value })}
                  placeholder="open"
                />
              </div>
            </div>

            <div className="flex gap-2 justify-end pt-4">
              <Button variant="outline" onClick={resetDestinationForm}>
                Annuler
              </Button>
              <Button onClick={handleDestinationSave} disabled={!destinationFormData.name}>
                {editingDestination ? "Mettre à jour" : "Créer"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminDestinations;
